import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import { LocalScheduleProvider } from './local-provider'
import { airportIndex, resolveLegs, type ResolveContext } from './resolve'
import { missingRows, type MissingRows } from './missing'
import { planImport } from './plan'
import {
  ScheduleContext,
  type PreparedFile,
  type ScheduleContextValue,
  type ScheduleStatus,
} from './schedule-context'
import type {
  FlightLeg,
  ImportBatch,
  ImportPlan,
  ParsedFile,
  ScheduleProvider as Provider,
} from './types'

/**
 * Loads the schedule and owns every write to it.
 *
 * Mounted by the board rather than by the shell: a month of flying is 12.000
 * rows, and no screen except the Gantt has any use for them. Same reasoning as
 * ReferenceDataProvider sitting inside the authenticated shell rather than at
 * the app root.
 *
 * The join to reference data happens here, in one place, because every leg
 * needs it and none of the UI should know how it is done: a file states local
 * times at stations, and only the airport and timezone tables can turn those
 * into the UTC instants everything downstream works in.
 */
export function ScheduleDataProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const actor = session?.user.email ?? 'unknown'
  const { airports, aircraft, zoneRules, status: referenceStatus } = useReference()

  const providerRef = useRef<Provider | null>(null)
  const [status, setStatus] = useState<ScheduleStatus>('loading')
  const [legs, setLegs] = useState<FlightLeg[]>([])
  const [batches, setBatches] = useState<ImportBatch[]>([])
  const [persisted, setPersisted] = useState(true)
  const [canUndo, setCanUndo] = useState(false)

  useEffect(() => {
    let cancelled = false
    const provider = providerRef.current ?? new LocalScheduleProvider()
    providerRef.current = provider
    provider
      .load()
      .then((snapshot) => {
        if (cancelled) return
        setLegs(snapshot.legs)
        setBatches(snapshot.batches)
        setPersisted(snapshot.persisted)
        setCanUndo(snapshot.canUndo)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const context = useMemo(
    () => ({
      airportsByIata: airportIndex(airports),
      zoneRules,
      fleetTails: new Set(aircraft.map((row) => row.regn)),
    }),
    [airports, aircraft, zoneRules],
  )

  const prepare = useCallback(
    (parsed: ParsedFile): PreparedFile => ({
      parsed,
      scope: parsed.scope,
      // Resolved once per file: a scope change moves which legs are replaced,
      // never what a leg says, so the preview stays instant while the user
      // adjusts the period.
      legs: resolveLegs(parsed.legs, context, 'pending'),
    }),
    [context],
  )

  const missing = useCallback(
    (files: PreparedFile[]): MissingRows =>
      missingRows(
        files.flatMap((file) => file.parsed.legs),
        aircraft,
        context.airportsByIata,
      ),
    [aircraft, context],
  )

  const plan = useCallback(
    (files: PreparedFile[]): ImportPlan => {
      const result = planImport(
        legs,
        files.map((file) => ({ scope: file.scope, legs: file.legs })),
        new Date().toISOString(),
      )
      return {
        ...result.plan,
        ...incomingIssues(files.flatMap((file) => file.legs), context.airportsByIata),
      }
    },
    [legs, context],
  )

  const commit = useCallback(
    async (files: PreparedFile[], created?: MissingRows): Promise<ImportPlan> => {
      const provider = providerRef.current
      if (!provider) throw new Error('Schedule provider not ready')

      const at = new Date().toISOString()
      const id = `imp-${at}`

      // Rows created a moment ago for this very import are folded into the
      // clock here rather than read back from the reference tables, which have
      // not re-rendered yet. A station created with a zone therefore places its
      // legs on this import, not on the next one.
      const augmented = withCreated(context, created)
      const scoped = files.map((file) => ({
        scope: file.scope,
        legs: created
          ? resolveLegs(file.parsed.legs, augmented, id)
          : file.legs.map((leg) => ({ ...leg, batchId: id })),
      }))
      const result = planImport(legs, scoped, at)
      const summary: ImportPlan = {
        ...result.plan,
        ...incomingIssues(
          scoped.flatMap((file) => file.legs),
          augmented.airportsByIata,
        ),
      }
      const batch: ImportBatch = {
        id,
        at,
        by: actor,
        files: files.map((file) => ({
          fileName: file.parsed.fileName,
          scope: file.scope,
          legs: file.legs.length,
        })),
        plan: summary,
      }

      const snapshot = await provider.apply(result.legs, batch)
      setLegs(snapshot.legs)
      setBatches(snapshot.batches)
      setPersisted(snapshot.persisted)
      setCanUndo(snapshot.canUndo)
      return summary
    },
    [actor, legs, context],
  )

  const undo = useCallback(async () => {
    const provider = providerRef.current
    if (!provider) return
    const snapshot = await provider.undo()
    setLegs(snapshot.legs)
    setBatches(snapshot.batches)
    setPersisted(snapshot.persisted)
    setCanUndo(snapshot.canUndo)
  }, [])

  const clear = useCallback(async () => {
    const provider = providerRef.current
    if (!provider) return
    const snapshot = await provider.clear()
    setLegs(snapshot.legs)
    setBatches(snapshot.batches)
    setPersisted(snapshot.persisted)
    setCanUndo(snapshot.canUndo)
  }, [])

  const value = useMemo<ScheduleContextValue>(
    () => ({
      // The board cannot resolve a leg until the airport and timezone tables
      // are in memory, so it waits on whichever of the two is slower.
      status: referenceStatus === 'error' ? 'error' : referenceStatus === 'ready' ? status : 'loading',
      providerName: providerRef.current?.name ?? '',
      legs,
      batches,
      persisted,
      canUndo,
      prepare,
      plan,
      missing,
      commit,
      undo,
      clear,
    }),
    [
      referenceStatus,
      status,
      legs,
      batches,
      persisted,
      canUndo,
      prepare,
      plan,
      missing,
      commit,
      undo,
      clear,
    ],
  )

  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>
}

/** The clock with this import's own new stations and tails already in it. */
function withCreated(context: ResolveContext, created?: MissingRows): ResolveContext {
  if (!created || (created.airports.length === 0 && created.aircraft.length === 0)) return context
  const airportsByIata = new Map(context.airportsByIata)
  for (const draft of created.airports) {
    airportsByIata.set(draft.iata, { timezone: draft.timezone.trim() })
  }
  return {
    ...context,
    airportsByIata,
    fleetTails: new Set([...context.fleetTails, ...created.aircraft.map((draft) => draft.regn)]),
  }
}

/** Data quality of the incoming files, counted once for the preview. */
function incomingIssues(incoming: FlightLeg[], known: Map<string, { timezone: string }>) {
  const unknownStations = new Set<string>()
  const unknownTails = new Set<string>()
  let unplaceable = 0

  for (const leg of incoming) {
    if (leg.issues.includes('unplaceable')) unplaceable += 1
    if (leg.issues.includes('unknownTail')) unknownTails.add(leg.regn)
    if (!leg.issues.includes('unknownStation')) continue
    if (!known.has(leg.dep)) unknownStations.add(leg.dep)
    if (!known.has(leg.arr)) unknownStations.add(leg.arr)
  }
  return {
    unknownStations: [...unknownStations].sort(),
    unknownTails: [...unknownTails].sort(),
    unplaceable,
  }
}
